/**
 * Home-screen install telemetry.
 *
 * Records each step of the install prompt (shown / tapped / accepted /
 * dismissed / installed) so the admin report can show how many visitors
 * actually add TruWe to their home screen. Written with the service-role
 * client because the ledger is admin-read-only.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const EventSchema = z.object({
  event: z.enum(["shown", "clicked", "accepted", "dismissed", "installed", "error"]),
  platform: z.enum(["android", "ios", "unknown"]).optional(),
  surface: z.string().max(40).optional(),
  route: z.string().max(300).optional(),
  anonymousId: z.string().max(64).optional(),
  userAgent: z.string().max(400).optional(),
});

export type RecordInstallPromptEventInput = z.input<typeof EventSchema>;

export const recordInstallPromptEvent = createServerFn({ method: "POST" })
  .inputValidator((data: RecordInstallPromptEventInput) => EventSchema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("pwa_install_events" as never).insert({
      event: data.event,
      platform: data.platform ?? null,
      surface: data.surface ?? null,
      route: data.route ?? null,
      anonymous_id: data.anonymousId ?? null,
      user_agent: data.userAgent ?? null,
    } as never);
    if (error) return { ok: false };
    return { ok: true };
  });
